import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  Collapse,
  Paper,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Switch from "@mui/material/Switch";
import {
  Close as CloseIcon,
  PersonAddAlt1 as PersonAddIcon,
  AccountBalanceOutlined as AccountBalanceIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { useTrainers, useAssignTrainers } from "../../../hooks/useTrainers";

// Custom modern iOS-like switch matching the exact design
const CustomSwitch = styled(Switch)(({ theme }) => ({
  width: 44,
  height: 24,
  padding: 0,
  display: "flex",
  "&:active": {
    "& .MuiSwitch-thumb": {
      width: 18,
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      transform: "translateX(20px)",
    },
  },
  "& .MuiSwitch-switchBase": {
    padding: 2,
    "&.Mui-checked": {
      transform: "translateX(20px)",
      color: "#ffffff",
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: "#2563EB",
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.2)",
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: "#ffffff",
    transition: theme.transitions.create(["width"], {
      duration: 200,
    }),
  },
  "& .MuiSwitch-track": {
    borderRadius: 24 / 2,
    opacity: 1,
    backgroundColor: "#CBD5E1",
    boxSizing: "border-box",
  },
}));

const AssignTrainerModal = ({ open, onClose, institute, onSuccess }) => {
  const [trainersList, setTrainersList] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const dropdownRef = useRef(null);

  // Custom hook wrapping React Query with automatic token interceptors & caching for /trainer_assign/all-trainers
  const { data: trainersData, isLoading: isTrainersLoading } = useTrainers({
    enabled: open,
  });

  // Custom mutation hook for assigning trainers
  const assignTrainersMutation = useAssignTrainers();

  // Reset or initialize list when modal opens or trainers query cache resolves
  useEffect(() => {
    if (open) {
      setDropdownOpen(false);
      setSearchQuery("");

      const assignedTrainerId =
        institute?.trainer_id != null ? Number(institute.trainer_id) : null;
      const assignedTrainerName =
        typeof institute?.trainer_name === "string"
          ? institute.trainer_name.toLowerCase().trim()
          : "";

      if (trainersData && Array.isArray(trainersData)) {
        const liveData = trainersData.map((t, idx) => {
          const tId = t?.trainer_id != null ? Number(t.trainer_id) : null;
          const tName = (t?.trainer_name || t?.fullname || t?.name || "")
            .toLowerCase()
            .trim();

          let isAssigned = false;
          if (assignedTrainerId && tId) {
            isAssigned = tId === assignedTrainerId;
          } else if (assignedTrainerName && tName) {
            isAssigned = tName === assignedTrainerName;
          }

          return {
            id: t?.trainer_id,
            name: t?.trainer_name || t?.fullname || t?.name || `Trainer ${idx + 1}`,
            assigned: isAssigned,
          };
        });
        setTrainersList(liveData);
      } else {
        setTrainersList([]);
      }
    }
  }, [open, trainersData, institute]);

  // Handle toggle switch change for a trainer (Single trainer selection only)
  const handleToggleTrainer = (trainerId, e) => {
    if (e) e.stopPropagation();
    setTrainersList((prev) =>
      prev.map((item) => {
        if (item.id === trainerId) {
          return { ...item, assigned: !item.assigned };
        }
        return { ...item, assigned: false };
      })
    );
  };

  // Handle Save
  const handleSave = async () => {
    try {
      const assignedTrainer = trainersList.find((t) => t.assigned);

      const payload = {
        institute_id: institute?.institue_id,
        trainer_id: assignedTrainer ? assignedTrainer.id : null,
      };

      console.log("Assign Trainer Payload:", payload);

      await assignTrainersMutation.mutateAsync(payload);

      setSnackbar({
        open: true,
        message: assignedTrainer
          ? "Trainer assigned successfully!"
          : "Trainer unassigned successfully!",
        severity: "success",
      });

      if (onSuccess) {
        onSuccess(payload);
      }

      setTimeout(() => {
        onClose();
      }, 600);
    } catch (error) {
      console.error("Error saving trainers:", error);
      const errorMsg =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update trainer assignment.";
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: "error",
      });
    }
  };

  const saving = assignTrainersMutation.isLoading;

  const instituteDisplayName =
    institute?.institute_name || "ABC Training Institute";

  const assignedTrainers = trainersList.filter((t) => t.assigned);

  const filteredTrainers = trainersList.filter((t) => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return true;
    return t.name.toLowerCase().includes(q);
  });

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow:
              "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
            overflow: "hidden",
            backgroundColor: "#FFFFFF",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Fixed Header Section */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            p: { xs: 2.5, sm: 3 },
            pb: 1.5,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {/* Blue icon circle */}
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                backgroundColor: "#EFF6FF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <PersonAddIcon sx={{ color: "#2563EB", fontSize: 26 }} />
            </Box>

            {/* Title and subtitle */}
            <Box>
              <Typography
                sx={{
                  fontSize: "18px",
                  fontWeight: 700,
                  color: "#111827",
                  lineHeight: 1.3,
                }}
              >
                Assign Trainer for Institutes
              </Typography>
              <Typography
                sx={{
                  fontSize: "13px",
                  color: "#64748B",
                  mt: 0.5,
                }}
              >
                Select a trainer and toggle to assign or unassign the trainer for the selected institute(s).
              </Typography>
            </Box>
          </Box>

          {/* Close Button */}
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: "#64748B",
              p: 0.5,
              "&:hover": { backgroundColor: "#F1F5F9", color: "#111827" },
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* Scrollable Form Body */}
        <Box
          sx={{
            px: { xs: 2.5, sm: 3 },
            py: 1.5,
            flex: 1,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
          }}
        >
          {/* Institute Name */}
          <Box>
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                color: "#1E293B",
                mb: 0.75,
              }}
            >
              Institute Name <span style={{ color: "#EF4444" }}>*</span>
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "#F8FAFC",
                border: "1px solid #E2E8F0",
                borderRadius: "8px",
                px: 1.75,
                py: 1.2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, width: "100%" }}>
                <AccountBalanceIcon sx={{ color: "#64748B", fontSize: 20 }} />
                <Typography
                  sx={{
                    fontSize: "13.5px",
                    fontWeight: 500,
                    color: "#1E293B",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {instituteDisplayName}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Single Unified Trainer Dropdown with Options Toggle */}
          <Box ref={dropdownRef}>
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                color: "#1E293B",
                mb: 0.75,
              }}
            >
              Trainer <span style={{ color: "#EF4444" }}>*</span>
            </Typography>

            {/* Dropdown Trigger Box */}
            <Box
              onClick={() => setDropdownOpen((prev) => !prev)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: "#FFFFFF",
                border: "1px solid",
                borderColor: dropdownOpen ? "#3B82F6" : "#E2E8F0",
                borderRadius: dropdownOpen ? "8px 8px 0 0" : "8px",
                px: 1.75,
                py: 1.2,
                cursor: "pointer",
                transition: "all 0.15s ease-in-out",
                "&:hover": {
                  borderColor: dropdownOpen ? "#3B82F6" : "#CBD5E1",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, flex: 1, pr: 1 }}>
                {assignedTrainers.length === 0 ? (
                  <Typography sx={{ color: "#94A3B8", fontSize: "13.5px", fontWeight: 400 }}>
                    Select Trainer
                  </Typography>
                ) : (
                  <Typography
                    sx={{
                      fontSize: "13.5px",
                      fontWeight: 600,
                      color: "#1E293B",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {assignedTrainers[0].name}
                  </Typography>
                )}
              </Box>
              <IconButton
                size="small"
                sx={{ color: "#64748B", p: 0.25, ml: 1 }}
              >
                {dropdownOpen ? (
                  <KeyboardArrowUpIcon sx={{ fontSize: 20 }} />
                ) : (
                  <KeyboardArrowDownIcon sx={{ fontSize: 20 }} />
                )}
              </IconButton>
            </Box>

            {/* Collapsible Dropdown Options Container with Toggles */}
            <Collapse in={dropdownOpen}>
              <Paper
                elevation={0}
                sx={{
                  border: "1px solid #E2E8F0",
                  borderTop: "none",
                  borderRadius: "0 0 8px 8px",
                  overflow: "hidden",
                  backgroundColor: "#FFFFFF",
                }}
              >
                {/* Search Bar inside Dropdown */}
                <Box sx={{ p: 1.25, backgroundColor: "#F8FAFC", borderBottom: "1px solid #E2E8F0" }}>
                  <TextField
                    size="small"
                    placeholder="Search trainer..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: 17, color: "#94A3B8" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#FFFFFF",
                        fontSize: "12.5px",
                        height: "34px",
                        borderRadius: "6px",
                        "& fieldset": { borderColor: "#E2E8F0" },
                        "&:hover fieldset": { borderColor: "#CBD5E1" },
                        "&.Mui-focused fieldset": { borderColor: "#3B82F6" },
                      },
                    }}
                  />
                </Box>

                {/* Table Header inside Dropdown */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    backgroundColor: "#F0F4FA",
                    px: 2,
                    py: 1,
                    borderBottom: "1px solid #E2E8F0",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#475569",
                    }}
                  >
                    Trainer Name
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#475569",
                    }}
                  >
                    Assign / Unassign
                  </Typography>
                </Box>

                {/* Trainer Option Rows with Toggles - Scrollable */}
                <Box
                  sx={{
                    maxHeight: "220px",
                    overflowY: "auto",
                    scrollbarWidth: "thin",
                    "&::-webkit-scrollbar": { width: "5px" },
                    "&::-webkit-scrollbar-track": { backgroundColor: "#F8FAFC" },
                    "&::-webkit-scrollbar-thumb": { backgroundColor: "#CBD5E1", borderRadius: "4px" },
                  }}
                >
                  {isTrainersLoading && trainersList.length === 0 ? (
                    <Box sx={{ py: 3, display: "flex", justifyContent: "center", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={18} sx={{ color: "#2563EB" }} />
                      <Typography sx={{ fontSize: "13px", color: "#64748B" }}>
                        Loading trainers...
                      </Typography>
                    </Box>
                  ) : filteredTrainers.length === 0 ? (
                    <Box sx={{ py: 3, textAlign: "center" }}>
                      <Typography sx={{ fontSize: "13px", color: "#94A3B8" }}>
                        No trainers found
                      </Typography>
                    </Box>
                  ) : (
                    filteredTrainers.map((trainer, index) => {
                      const isLast = index === filteredTrainers.length - 1;

                      return (
                        <Box
                          key={trainer.id}
                          onClick={() => handleToggleTrainer(trainer.id)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            px: 2,
                            py: 1.25,
                            cursor: "pointer",
                            borderBottom: isLast ? "none" : "1px solid #F1F5F9",
                            backgroundColor: trainer.assigned ? "#F8FAFC" : "#FFFFFF",
                            "&:hover": { backgroundColor: "#F1F5F9" },
                            transition: "background-color 0.15s ease",
                          }}
                        >
                          {/* Left: Trainer Name */}
                          <Typography
                            sx={{
                              fontSize: "13.5px",
                              fontWeight: trainer.assigned ? 600 : 500,
                              color: "#1E293B",
                            }}
                          >
                            {trainer.name}
                          </Typography>

                          {/* Right: Switch & Status */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              flexShrink: 0,
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <CustomSwitch
                              checked={Boolean(trainer.assigned)}
                              onChange={(e) => handleToggleTrainer(trainer.id, e)}
                            />
                            <Box sx={{ width: "75px" }}>
                              {trainer.assigned ? (
                                <Typography
                                  sx={{
                                    fontSize: "12px",
                                    fontWeight: 500,
                                    color: "#16A34A",
                                    backgroundColor: "#F0FDF4",
                                    px: 1,
                                    py: 0.3,
                                    borderRadius: "6px",
                                    textAlign: "center",
                                    display: "inline-block",
                                    width: "100%",
                                  }}
                                >
                                  Assigned
                                </Typography>
                              ) : (
                                <Typography
                                  sx={{
                                    fontSize: "12px",
                                    fontWeight: 500,
                                    color: "#64748B",
                                    backgroundColor: "#F8FAFC",
                                    px: 1,
                                    py: 0.3,
                                    borderRadius: "6px",
                                    textAlign: "center",
                                    display: "inline-block",
                                    width: "100%",
                                  }}
                                >
                                  Unassigned
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      );
                    })
                  )}
                </Box>
              </Paper>
            </Collapse>
          </Box>
        </Box>

        {/* Fixed Bottom Action Buttons Footer */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 1.5,
            p: { xs: 2.5, sm: 3 },
            pt: 2,
            borderTop: "1px solid #F1F5F9",
            backgroundColor: "#FFFFFF",
            flexShrink: 0,
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={saving}
            sx={{
              borderColor: "#E2E8F0",
              color: "#374151",
              backgroundColor: "#FFFFFF",
              borderRadius: "8px",
              textTransform: "none",
              fontSize: "13.5px",
              fontWeight: 500,
              px: 2.75,
              py: 0.75,
              "&:hover": {
                borderColor: "#CBD5E1",
                backgroundColor: "#F8FAFC",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              backgroundColor: "#2563EB",
              color: "#FFFFFF",
              borderRadius: "8px",
              textTransform: "none",
              fontSize: "13.5px",
              fontWeight: 500,
              px: 3.5,
              py: 0.75,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#1D4ED8",
                boxShadow: "none",
              },
            }}
          >
            {saving ? (
              <CircularProgress size={18} sx={{ color: "#FFFFFF" }} />
            ) : (
              "Save"
            )}
          </Button>
        </Box>
      </Dialog>

      {/* Snackbar Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%", borderRadius: "8px" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AssignTrainerModal;
